import * as React from 'react';

interface TourCutoutProps {
  /** Null renders a plain full-screen scrim with no hole. */
  rect: DOMRect | null;
  padding?: number;
  onScrimClick?: () => void;
}

/**
 * The dimmed backdrop with a hole punched over the anchor.
 *
 * Four rectangles around the anchor rather than an SVG mask or a giant
 * box-shadow: the hole is then a genuine gap in the overlay, so the element
 * underneath stays clickable — which the DiDi tour wants, since a step points
 * at the row the user is about to click.
 */
export const TourCutout: React.FC<TourCutoutProps> = ({ rect, padding = 6, onScrimClick }) => {
  const panels: React.CSSProperties[] = [];

  if (!rect) {
    panels.push({ inset: 0 });
  } else {
    const top = Math.max(0, rect.top - padding);
    const left = Math.max(0, rect.left - padding);
    const right = Math.min(window.innerWidth, rect.right + padding);
    const bottom = Math.min(window.innerHeight, rect.bottom + padding);

    panels.push({ top: 0, left: 0, right: 0, height: top });
    panels.push({ top: bottom, left: 0, right: 0, bottom: 0 });
    panels.push({ top, left: 0, width: left, height: bottom - top });
    panels.push({ top, left: right, right: 0, height: bottom - top });
  }

  return (
    <div className="dd-tour__scrim-layer" data-testid="dd-tour-scrim">
      {panels.map((style, i) => (
        <div
          key={i}
          className="dd-tour__scrim"
          style={{ position: 'fixed', ...style }}
          onClick={onScrimClick}
        />
      ))}
      {rect && (
        <div
          className="dd-tour__ring"
          style={{
            position: 'fixed',
            top: Math.max(0, rect.top - padding),
            left: Math.max(0, rect.left - padding),
            width: rect.width + padding * 2,
            height: rect.height + padding * 2,
          }}
        />
      )}
    </div>
  );
};

export default TourCutout;
